/**
 * Button (§18). Variants carry meaning, not decoration:
 *   - primary   → the one main action on a surface (accent fill)
 *   - secondary → standard action (raised surface + border)
 *   - ghost     → low-emphasis / toolbar action (transparent)
 *   - danger    → destructive (red) — none wired in this read-only increment
 * Sizes follow the active density (`--ui-control-h`). Renders a real <button>;
 * keyboard focus shows the shared focus ring.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "./Icon.js";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon name. */
  icon?: IconName;
  /** Render as a square icon-only button (provide `aria-label`). */
  iconOnly?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  iconOnly = false,
  block = false,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  const cls = [
    "btn",
    `btn--${variant}`,
    `btn--${size}`,
    iconOnly ? "btn--icon" : "",
    block ? "btn--block" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type={type} className={cls} {...rest}>
      {icon && <Icon name={icon} />}
      {!iconOnly && children}
    </button>
  );
}
